import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User, Activity, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { HeadToHeadModal } from './HeadToHeadModal';
import { MatchFeedTab } from './MatchFeedTab';

interface PlayerProfileModalProps {
  playerId: string | null;
  onClose: () => void;
}

export const PlayerProfileModal = ({ playerId, onClose }: PlayerProfileModalProps) => {
  const [profileData, setProfileData] = useState<any>(null);
  const [performance, setPerformance] = useState<{trMatches: any[], compMatches: any[]}>({ trMatches: [], compMatches: [] });
  const [loading, setLoading] = useState(true);
  
  const [filterType, setFilterType] = useState('ALL');
  const [filterSource, setFilterSource] = useState('ALL');
  const [showH2HModal, setShowH2HModal] = useState(false);

  const { currentUser } = useAuth();

  useEffect(() => {
    if (!playerId) return;
    
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL || "https://techtennis-api.vercel.app"}/api/users/${playerId}/public-profile`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('tt_auth_token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          setProfileData(data.user);
          setPerformance({ trMatches: data.trMatches || [], compMatches: data.compMatches || [] });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [playerId]);

  if (!playerId) return null;

  // Calculate age from birthDate
  const birthDateStr = profileData?.birthDate;
  let ageStr = 'Não informada';
  if (birthDateStr) {
    const bd = new Date(birthDateStr);
    if (!isNaN(bd.getTime())) {
      const diff = Date.now() - bd.getTime();
      const age = new Date(diff).getUTCFullYear() - 1970;
      ageStr = `${age} anos`;
    }
  }

  const allMatches = [...performance.trMatches, ...performance.compMatches].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  
  const filteredMatches = allMatches.filter(m => {
    if (filterType !== 'ALL') {
      const isDoubles = m.match_type?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes('dupla');
      if (filterType === 'SIMPLES' && isDoubles) return false;
      if (filterType === 'DUPLAS' && !isDoubles) return false;
    }
    if (filterSource !== 'ALL') {
      if (m.source !== filterSource) return false;
    }
    return true;
  }).slice(0, 10);

  const wins = filteredMatches.filter(m => m.winner_id === playerId).length;
  const losses = filteredMatches.length - wins;
  const winRate = filteredMatches.length > 0 ? Math.round((wins / filteredMatches.length) * 100) : 0;

  return createPortal(
    <>
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 10000,
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1A112C', borderRadius: '12px',
        width: '100%', maxWidth: '800px', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', color: 'white',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            {profileData?.photoUrl ? (
              <img src={profileData.photoUrl} alt="Avatar" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <User size={24} />
            )}
            {profileData?.name || 'Perfil do Jogador'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {currentUser && currentUser.id !== playerId && profileData && (
              <button 
                onClick={() => setShowH2HModal(true)}
                style={{ background: '#3A8E58', border: 'none', color: 'white', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                Ver H2H Comigo
              </button>
            )}
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}>
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {loading ? (
            <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', padding: '40px' }}>Carregando perfil...</p>
          ) : (
            <>
              {/* Perfil Info */}
              <div style={{ background: 'rgba(255,255,255,0.05)', padding: '24px', borderRadius: '12px' }}>
                <h3 style={{ margin: 0, marginBottom: '16px', fontSize: '18px', color: 'rgba(255,255,255,0.9)' }}>Dados do Jogador</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Idade</label>
                    <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '14px' }}>{ageStr}</div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Altura (m)</label>
                    <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '14px' }}>{profileData?.height || '-'}</div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Peso (kg)</label>
                    <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '14px' }}>{profileData?.weight || '-'}</div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Lateralidade</label>
                    <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '14px' }}>{profileData?.handedness || '-'}</div>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Modelo da Raquete</label>
                    <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '14px' }}>{profileData?.racketModel || '-'}</div>
                  </div>
                </div>
              </div>

              {/* Desempenho */}
              <div style={{ background: 'rgba(255,255,255,0.05)', padding: '24px', borderRadius: '12px' }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '18px' }}><Activity size={20} /> Desempenho (Últimos 10 Jogos)</h3>
                
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                  <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white' }}>
                    <option value="ALL">Simples + Duplas</option>
                    <option value="SIMPLES">Apenas Simples</option>
                    <option value="DUPLAS">Apenas Duplas</option>
                  </select>
                  <select value={filterSource} onChange={e => setFilterSource(e.target.value)} style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white' }}>
                    <option value="ALL">Ranking + Torneios</option>
                    <option value="RANKING">Apenas Ranking</option>
                    <option value="TOURNAMENT">Apenas Torneios</option>
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '24px', marginBottom: '32px' }}>
                  <div style={{ flex: 1, background: 'rgba(0,0,0,0.2)', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: 'var(--primary-color)' }}>{winRate}%</div>
                    <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>Taxa de Vitória</div>
                  </div>
                  <div style={{ flex: 1, background: 'rgba(0,0,0,0.2)', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#4CAF50' }}>{wins}</div>
                    <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>Vitórias</div>
                  </div>
                  <div style={{ flex: 1, background: 'rgba(0,0,0,0.2)', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#F44336' }}>{losses}</div>
                    <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>Derrotas</div>
                  </div>
                </div>

                <h4 style={{ fontSize: '16px', marginBottom: '16px', color: 'rgba(255,255,255,0.8)' }}>Histórico de Partidas</h4>
                <div style={{ margin: '0 -20px' }}>
                  <MatchFeedTab 
                    competitionId="" 
                    currentUser={currentUser} 
                    filterPlayerId={playerId} 
                    readOnly={true} 
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
      {showH2HModal && currentUser && profileData && (
        <HeadToHeadModal
          player1Id={currentUser.id}
          player2Id={playerId}
          player1Name={currentUser.name || 'Você'}
          player2Name={profileData.name || 'Adversário'}
          onClose={() => setShowH2HModal(false)}
        />
      )}
    </>,
    document.body
  );
};
